import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast, ToastContainer } from 'react-toastify';
import CreateRegistrationModel from './create-registration-model';
import { useDebounce } from '../../hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import {
  exportRegistrations,
  getByOrganizationIdAndCategory,
  removeRegistration,
  uploadRegistrations,
} from 'services/registrations';
import RegistrationDetailsModal from './registration-details-modal';
import { generateFilename } from 'utility/filenameGenerator';
import { getNavigationBreadcrumbs } from 'utility/breadcrumbUtils';
import BulkExcelActions, {
  BulkExcelLoadingOverlay,
} from 'Components/Common/BulkExcelActions';
import { useBulkExcelOperations } from 'utility/excelBulkOperations';

const RegistrationBoard = () => {
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const mazarName = searchParams.get('mazar_name') || '';
  const category = searchParams.get('category') || '';
  const breadCrumbItems = getNavigationBreadcrumbs({
    organizationId: id || '',
    mazarName,
    category,
  });

  if (breadCrumbItems.length) {
    breadCrumbItems[breadCrumbItems.length - 1].isActive = true;
  }

  const [regs, setRegs] = useState<any[]>([]);
  const [modalInfo, setModalInfo] = useState({
    isOpen: false,
    editData: null,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedReg, setSelectedReg] = useState<any | null>(null);

  const toggle = useCallback(() => {
    if (modalInfo.isOpen) {
      setModalInfo({ ...modalInfo, isOpen: false, editData: null });
    } else {
      setModalInfo({ ...modalInfo, isOpen: true });
    }
  }, [modalInfo]);

  const toggleDetails = useCallback(() => {
    setDetailsModal(prev => !prev);
  }, []);

  const loadData = async () => {
    dispatch(isVisible(true));
    try {
      // Replace with real API call using id & category & search
      const list = await getByOrganizationIdAndCategory(Number(id), category);
      setRegs(list.data as any);
    } catch (e: any) {
      toast.error(`Error loading registrations: ${e}`);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const { loading, handleExport, handleUpload } = useBulkExcelOperations({
    exportFile: () => exportRegistrations(Number(id), category),
    getExportFilename: () =>
      generateFilename({
        organizationName: mazarName || 'Mazar',
        category: category || 'unregistered',
      }),
    uploadFile: uploadRegistrations,
    onUploadSuccess: () => loadData(),
  });

  const handleDelete = async (regId: number) => {
    if (!window.confirm('Are you sure you want to delete this registration?'))
      return;
    dispatch(isVisible(true));
    try {
      await removeRegistration(regId);
      toast.success('Registration deleted');
      loadData();
    } catch (e: any) {
      toast.error(`Delete failed: ${e}`);
    } finally {
      dispatch(isVisible(false));
    }
  };

  useEffect(() => {
    loadData();
  }, [debouncedSearchQuery]);

  return (
    <React.Fragment>
      {loading && <BulkExcelLoadingOverlay />}
      <div className="page-content">
        <ToastContainer closeButton={false} limit={1} />
        <BreadCrumb
          title="Registration Board"
          breadcrumbItems={breadCrumbItems}
        />
        <Row className="g-4 mb-3">
          <div className="col-sm-auto">
            <div>
              <button
                type="button"
                className="btn btn-primary add-btn"
                onClick={toggle}
              >
                <i className="ri-add-line align-bottom me-1"></i> Add
                Registration
              </button>
            </div>

            <BulkExcelActions
              helpTargetId="registration-board-bulk-upload-help"
              onDownload={handleExport}
              onUpload={handleUpload}
              disabled={loading}
              downloadDisabled={loading || regs.length < 1}
            />
          </div>
          <div className="col-sm-3 ms-auto">
            <div className="d-flex justify-content-sm-end gap-2">
              <div className="search-box ms-2 col-sm-10">
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Search Registration"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </div>
          </div>
        </Row>
        <Row>
          {regs.length === 0 && (
            <p className="text-center mt-4">No registrations found</p>
          )}
          {regs.map((item, key) => (
            <Col xxl={4} sm={6} key={key} className="project-card">
              <Card className="card-height-100 position-relative">
                <UncontrolledDropdown className="position-absolute end-0 me-2 mt-2">
                  <DropdownToggle
                    tag="button"
                    className="btn btn-sm btn-light"
                    aria-expanded="false"
                  >
                    <i className="ri-more-2-fill"></i>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem
                      onClick={() => {
                        setSelectedReg(item);
                        setDetailsModal(true);
                      }}
                    >
                      View Details
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        setModalInfo({ isOpen: true, editData: { ...item } });
                      }}
                    >
                      Edit
                    </DropdownItem>
                    <DropdownItem
                      className="text-danger"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
                <CardBody>
                  <h5
                    className="mb-2 cursor-pointer text-truncate"
                    onClick={() =>
                      navigate(
                        `/organizations/${id}/registration-board/${item.id}/lands?mazar_name=${mazarName}&category=${category}&trustRegNumber=${item.trustRegNumber}`,
                      )
                    }
                  >
                    {item.trustRegNumber}
                  </h5>
                  <p
                    className="text-muted mb-1 text-truncate"
                    title={item.purposeOfTrust}
                  >
                    Purpose: {item.purposeOfTrust}
                  </p>
                  <p
                    className="text-muted mb-0 text-truncate"
                    title={item.soleTrustee}
                  >
                    Sole Trustee: {item.soleTrustee}
                  </p>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <CreateRegistrationModel
          isOpen={modalInfo.isOpen}
          toggle={toggle}
          type={category}
          organizationId={Number(id)}
          organizationName={mazarName || ''}
          onSuccess={loadData}
          editData={modalInfo.editData}
        />
        <RegistrationDetailsModal
          isOpen={detailsModal}
          toggle={toggleDetails}
          data={selectedReg}
        />
      </div>
    </React.Fragment>
  );
};

export default RegistrationBoard;
